import React, {
  useState,
  useEffect,
  useId,
  forwardRef,
  Children,
} from "react";
import Dropdown from "olive-react-myoneui/lib/Dropdown";
import Form from "olive-react-myoneui/lib/Form";
import FloatingLabel from "olive-react-myoneui/lib/FloatingLabel";

// Custom toggle to handle dropdown toggle actions
const CustomToggle = forwardRef(({ children, onClick }, ref) => (
  <div
    role="button"
    tabIndex={0}
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
  </div>
));

// Custom menu to render dropdown items
const CustomMenu = forwardRef(
  ({ children, style, className, value, "aria-labelledby": labeledBy }, ref) => (
    <div ref={ref} style={style} className={className} aria-labelledby={labeledBy}>
      <ul className="list-unstyled mb-0">
        {Children.toArray(children).filter((child) =>
          !value || child.props?.children?.toString().toLowerCase().includes(value)
        )}
      </ul>
    </div>
  )
);

const DropdownCommon = ({
  label = "Choose Option",
  className = "",
  items = [],
  disabled = false,
  defaultValue = "",
  value,
  floatingInput = true,
  onChange = () => {},
}) => {
  const [menuValue, setMenuValue] = useState(""); // For search input
  const [show, setShow] = useState(false); // Dropdown visibility
  const [selected, setSelected] = useState(null); // Selected item
  const [highlightedIndex, setHighlightedIndex] = useState(-1); // For keyboard navigation
  const uniqueId = useId(); // Unique ID for accessibility

  // Initialize selected state from defaultValue
  useEffect(() => {
    if (defaultValue && !value) {
      const selectedItem = items.find((item) => item.name === defaultValue);
      if (selectedItem) setSelected(selectedItem);
    }
  }, [defaultValue, items]);

  // Sync selected state with controlled value
  useEffect(() => {
    if (value) {
      setSelected(value);
    }
  }, [value]);

  const onSelect = (index) => {
    const selectedItem = items[index];
    setSelected(selectedItem);
    onChange(selectedItem); // Notify parent component
  };

  const onToggle = (isOpen) => {
    setShow(isOpen);
    if (!isOpen) {
      setMenuValue(""); // Clear search input on close
    }
  };

  const onInputChange = (e) => {
    const val = e.target.value.trim().toLowerCase();
    setMenuValue(val);
    setShow(true); // Open dropdown during search
  };

  const onInputKeyUp = (e) => {
    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) => Math.min(prev + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightedIndex > -1) {
      onSelect(highlightedIndex);
      setShow(false); // Close dropdown on selection
    }
  };

  return (
    <Dropdown
      className={`dropdown-select ${className}`}
      onSelect={onSelect}
      show={show}
      onToggle={onToggle}
      disabled={disabled}
    >
      <Dropdown.Toggle as={CustomToggle} id={`dropdown-custom-components-${uniqueId}`}>
        {floatingInput ? (
          <FloatingLabel
            controlId={`floatingSelection-${uniqueId}`}
            label={label}
            className="w-100"
          >
            <Form.Control
              type="text"
              placeholder={label}
              onChange={onInputChange}
              onKeyDown={onInputKeyUp}
              value={selected?.name || ""}
              disabled={disabled}
            />
            <div className="caret-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="6.119"
                height="11.658"
                viewBox="0 0 6.119 11.658"
              >
                <path
                  id="Path_21616"
                  data-name="Path 21616"
                  d="M0,0,4.42,3.71,8.84,0"
                  transform="translate(1.409 10.249) rotate(-90)"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </div>
          </FloatingLabel>
        ) : (
          <Form.Group className="mb-4 w-100 myk__stu-simple-dropdown">
            <Form.Label>{label}</Form.Label>
            <Form.Control
              type="text"
              placeholder={label}
              onChange={onInputChange}
              onKeyDown={onInputKeyUp}
              value={selected?.name || ""}
              disabled={disabled}
            />
            <div className="caret-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="6.119"
                height="11.658"
                viewBox="0 0 6.119 11.658"
              >
                <path
                  id="Path_21616"
                  data-name="Path 21616"
                  d="M0,0,4.42,3.71,8.84,0"
                  transform="translate(1.409 10.249) rotate(-90)"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </div>
          </Form.Group>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu value={menuValue} as={CustomMenu}>
        {items.map((item, i) => (
          <Dropdown.Item
            key={i}
            eventKey={i}
            active={i === highlightedIndex || selected?.name === item.name}
            onClick={() => onSelect(i)}
          >
            {item.name}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default DropdownCommon;
